import { Browser } from '@capacitor/browser';
import { defaultParseSearch, NavigateOptions, ToOptions, useNavigate, useRouter } from '@tanstack/react-router';

import { getCoreConfig } from '../config/config';
import { baseUrl } from '../utils/url';
import { useNavigationConfig } from './NavigationConfigProvider';

export type RouteTransitionAnimation =
  | 'push-on-new'
  | 'pull-off-old'
  | 'nav-slide-in-from-left'
  | 'nav-slide-in-from-right'
  | 'slide-up-from-bottom'
  | 'slide-down-to-bottom';

export const IN_APP_BROWSER_FLAG = 'inappbrowser';

type Target = '_self' | '_blank';
type NavigateParams = ToOptions & NavigateOptions;
interface UseNavigation {
  openURL: (destination: string | NavigateParams, animation?: RouteTransitionAnimation) => void;
  openInApp: (url: string, target?: Target) => void;
  goBack: (animation?: RouteTransitionAnimation) => void;
}

const addInAppBrowserFlag = (urlString: string): string => {
  let url;
  try {
    url = new URL(urlString);
  } catch (e) {
    url = new URL(`${baseUrl}${urlString}`);
  }
  url.searchParams.set(IN_APP_BROWSER_FLAG, 'true');
  return url.toString();
};

export const useNavigation = (): UseNavigation => {
  const navigate = useNavigate();
  const router = useRouter();
  const { history } = router;
  const { suppressViewTransitions } = useNavigationConfig();

  const getViewTransition = (animation?: RouteTransitionAnimation) => {
    if (suppressViewTransitions) return false;
    return animation ? { types: [animation] } : true;
  };

  return {
    openURL: (destination: string | NavigateParams, animation) => {
      if (typeof destination === 'string') {
        if (destination.indexOf('/') === 0) {
          if (destination.indexOf('?') === -1) {
            navigate({ to: destination, viewTransition: getViewTransition(animation) });
          } else {
            const url = new URL(destination, window.location.origin);
            const parseSearch = router.options.parseSearch ?? defaultParseSearch;
            const structuredDestination: NavigateParams = {
              to: url.pathname as ToOptions['to'],
              search: parseSearch(url.search),
              hash: url.hash ? url.hash.slice(1) : undefined,
            };
            navigate({ ...structuredDestination, viewTransition: getViewTransition(animation) });
          }
        } else if (destination.indexOf('mailto:') === 0 || destination.indexOf('tel:') === 0) {
          window.location.href = destination;
        } else {
          Browser.open({ url: destination }).then();
        }
      } else {
        navigate({ ...destination, viewTransition: getViewTransition(animation) });
      }
    },
    openInApp: (url: string, target?: Target) => {
      if (getCoreConfig().isNative) {
        Browser.open({ url: addInAppBrowserFlag(url) });
      } else {
        window.open(url, target || '_blank');
      }
    },
    goBack: history
      ? (animation?: RouteTransitionAnimation) => {
          if (animation && 'startViewTransition' in document) {
            document.startViewTransition({
              update: () => history.back(),
              types: [animation],
            });
          } else {
            history.back();
          }
        }
      : () => {},
  };
};
